import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, ArrowLeft, Check, Building2, FileText, Sparkles, Plus, Trash2, X, User, Loader2, AlertCircle, Gift, Star, Video, TrendingUp, Users, CreditCard, ChevronDown, ChevronUp, Package, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { CountrySelect, ImageUploadWithCrop, PhoneInput } from '@clareefai/ui';
import { useGetVendorMeQuery, useGetUseCaseQuestionsQuery, useUpdateUseCaseQuestionsMutation, useGetReviewQuestionsQuery, useUpdateReviewQuestionsMutation, useUpdateVendorMutation, useGetRewardSettingsQuery, useUpdateRewardSettingsMutation } from '../store/vendorApi';
import type { RewardConfig, VendorResponse } from '@clareefai/types';
import DiscountRewardModal, { buildDiscountRewardName, type DiscountReward } from '../components/rewards/DiscountRewardModal';
import { uploadFile } from '../utils/upload';
import { getIconHorseUrl } from '@clareefai/utils';

type OnboardingStep = 1 | 2 | 3 | 4 | 5 | 6;

const ADDRESS_DELIMITER = ' | ';

/** Backend returns address as single string "street | zip | city | country". Parse for prefill only. */
function parseAddress(addr: string | null | undefined): { street: string; zip: string; city: string; country: string } {
  if (!addr || !addr.trim()) return { street: '', zip: '', city: '', country: '' };
  if (addr.includes(ADDRESS_DELIMITER)) {
    const parts = addr.split(ADDRESS_DELIMITER);
    return {
      street: parts[0]?.trim() ?? '',
      zip: parts[1]?.trim() ?? '',
      city: parts[2]?.trim() ?? '',
      country: parts[3]?.trim() ?? ''
    };
  }
  return { street: addr.trim(), zip: '', city: '', country: '' };
}

function formatAddress(street: string, zip: string, city: string, country: string): string {
  const parts = [street.trim(), zip.trim(), city.trim(), country.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(ADDRESS_DELIMITER) : '';
}

function resolveAddressParts(
  source:
    | { streetAddress?: string | null; zipCode?: string | null; city?: string | null; country?: string | null; address?: string | null }
    | null
    | undefined
): { street: string; zip: string; city: string; country: string } {
  if (!source) return { street: '', zip: '', city: '', country: '' };
  if (source.streetAddress || source.zipCode || source.city || source.country) {
    return {
      street: source.streetAddress ?? '',
      zip: source.zipCode ?? '',
      city: source.city ?? '',
      country: source.country ?? ''
    };
  }
  return parseAddress(source.address);
}

const LINKEDIN_COMPANY_REGEX = /^https:\/\/(www\.)?linkedin\.com\/company\/[a-zA-Z0-9_-]+/;
const LINKEDIN_PERSON_REGEX = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9_-]+/;
function isValidLinkedInCompanyUrl(url: string): boolean {
  return LINKEDIN_COMPANY_REGEX.test(url?.trim() ?? '');
}
function isValidLinkedInPersonUrl(url: string): boolean {
  return LINKEDIN_PERSON_REGEX.test(url?.trim() ?? '');
}

interface Step1FormData {
  first_name: string;
  last_name: string;
  job_title: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  vendorLinkedin: string;
  companyLinkedin: string;
  companyName: string;
  businessIdentificationNumber: string;
  description: string;
  website: string;
  logo: string;
  avatar: string;
  billingContact: {
    firstName: string;
    lastName: string;
    email: string;
    street: string;
    zip: string;
    city: string;
    country: string;
    phone: string;
  };
}

const emptyStep1Form: Step1FormData = {
  first_name: '',
  last_name: '',
  job_title: '',
  street: '',
  zip: '',
  city: '',
  country: '',
  phone: '',
  email: '',
  vendorLinkedin: '',
  companyLinkedin: '',
  companyName: '',
  businessIdentificationNumber: '',
  description: '',
  website: '',
  logo: '',
  avatar: '',
  billingContact: {
    firstName: '',
    lastName: '',
    email: '',
    street: '',
    zip: '',
    city: '',
    country: '',
    phone: ''
  }
};

function getStep1FormFromVendor(vendor: VendorResponse): Step1FormData {
  const mainAddr = resolveAddressParts(vendor);
  const bcAddr = resolveAddressParts(vendor.billingContact);
  return {
    first_name: vendor.firstName ?? '',
    last_name: vendor.lastName ?? '',
    job_title: vendor.jobTitle ?? '',
    street: mainAddr.street,
    zip: mainAddr.zip,
    city: mainAddr.city,
    country: mainAddr.country,
    phone: vendor.phone ?? '',
    email: vendor.email ?? '',
    vendorLinkedin: vendor.vendorLinkedin ?? '',
    companyLinkedin: vendor.companyLinkedin ?? '',
    companyName: vendor.companyName ?? '',
    businessIdentificationNumber: (vendor as { businessIdentificationNumber?: string }).businessIdentificationNumber ?? '',
    description: vendor.description ?? '',
    website: vendor.pageInfo?.companyUrl ?? '',
    logo: vendor.logo ?? '',
    avatar: vendor.avatar ?? '',
    billingContact: vendor.billingContact
      ? {
          firstName: vendor.billingContact.firstName,
          lastName: vendor.billingContact.lastName,
          email: vendor.billingContact.email,
          street: bcAddr.street,
          zip: bcAddr.zip,
          city: bcAddr.city,
          country: bcAddr.country,
          phone: vendor.billingContact.phone ?? ''
        }
      : emptyStep1Form.billingContact
  };
}

type AnswerType = 'one_selected' | 'many_selected' | 'free_text' | 'numeral' | 'product_select';

interface Choice {
  id: string;
  text: string;
}

interface UseCaseQuestion {
  id: string;
  question: string;
  choices: Choice[];
  answerType: AnswerType;
}

const toChoices = (texts: string[]): Choice[] => texts.map(t => ({ id: crypto.randomUUID(), text: t }));

/**
 * Only questions passing this check are persisted on save. Every place that
 * counts or lists questions (step validation, recap step) must use the same
 * rule, otherwise the UI shows questions that silently disappear after saving.
 */
const isQuestionSavable = (q: UseCaseQuestion, productNames: string[]): boolean => {
  if (q.question.trim() === '') return false;
  if (q.answerType === 'free_text' || q.answerType === 'numeral') return true;
  if (q.answerType === 'product_select') return productNames.length >= 1;
  return q.choices.length >= 2 && q.choices.every(c => c.text.trim() !== '');
};

/** Default template questions shown when vendor has no questions yet (onboarding). */
const DEFAULT_USE_CASE_QUESTIONS: UseCaseQuestion[] = [
  {
    id: 'default-1',
    question: "What's your role and team size?",
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-2',
    question: 'Which product/module did you use?',
    choices: [],
    // product_select fills its choices from the vendor's products on save —
    // a plain choice type here would have 0 choices and be dropped by the
    // savable check, making the question vanish after completing onboarding.
    answerType: 'product_select',
  },
  {
    id: 'default-3',
    question: 'What problem were you trying to solve before using our solution?',
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-4',
    question: 'What were you using before (tool/process/vendor), and why did it fall short?',
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-5',
    question: 'What were the top 1–3 reasons you chose us?',
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-6',
    question: 'How did you implement it (time to value, who was involved, any blockers)?',
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-7',
    question: 'Which features did you use most, and for what workflow?',
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-8',
    question: 'What measurable results did you get? (numbers if possible: win rate, sales cycle, hours saved, pipeline influenced, revenue)',
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-9',
    question: 'Can you share one concrete example/story where our solution helped you with a difficult task?',
    choices: [],
    answerType: 'free_text',
  },
  {
    id: 'default-10',
    question: 'Who would you recommend us to, and in what situation?',
    choices: [],
    answerType: 'free_text',
  },
];

const DEFAULT_REVIEW_QUESTIONS: UseCaseQuestion[] = [
  { id: 'rev-1', question: 'What challenge were you trying to solve before working with [Vendor]?', choices: [], answerType: 'free_text' },
  { id: 'rev-2', question: 'Before [Vendor], how were you handling this need?', choices: [], answerType: 'free_text' },
  { id: 'rev-3', question: 'What made you choose [Vendor] over other options?', choices: [], answerType: 'free_text' },
  { id: 'rev-4', question: 'What has stood out most in your experience with [Vendor] so far?', choices: [], answerType: 'free_text' },
  { id: 'rev-5', question: 'What results or improvements have you seen so far?', choices: [], answerType: 'free_text' },
  { id: 'rev-6', question: 'Who would you recommend [Vendor] to, and why?', choices: [], answerType: 'free_text' },
  { id: 'rev-7', question: 'How would you rate your overall experience with [Vendor]?', choices: toChoices(['Excellent', 'Very good', 'Good', 'Fair', 'Poor']), answerType: 'one_selected' },
  { id: 'rev-8', question: 'How likely are you to recommend [Vendor] to a colleague or partner?', choices: toChoices(['Extremely likely', 'Very likely', 'Likely', 'Somewhat likely', 'Not likely']), answerType: 'one_selected' },
];

// ---------------------------------------------------------------------------
// Draft persistence — saves onboarding state to localStorage so a refresh
// never loses progress. Cleared only when onboarding is fully completed.
// ---------------------------------------------------------------------------

const DRAFT_VERSION = 4;
const DRAFT_KEY = 'onboarding_draft';

interface OnboardingDraft {
  version: number;
  savedAt: string;
  currentStep: OnboardingStep;
  step1Form: Step1FormData;
  billingSameAsAbove: boolean;
  step1Expanded: { basic: boolean; company: boolean; billing: boolean };
  useCaseQuestions: UseCaseQuestion[];
  reviewQuestions: UseCaseQuestion[];
  products: Array<{ name: string; description: string }>;
  noProducts: boolean;
  rewardCurrency: 'EUR' | 'USD' | 'GBP';
  rewardConfigs: RewardConfig[];
}

function loadDraft(): OnboardingDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OnboardingDraft;
    if (parsed.version !== DRAFT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveDraft(draft: OnboardingDraft): void {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Storage quota exceeded or unavailable — fail silently.
  }
}

function clearDraft(): void {
  localStorage.removeItem(DRAFT_KEY);
}

// ---------------------------------------------------------------------------

const Onboarding: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(1);
  // True once the user has clicked Continue while required fields were still
  // missing — drives the red-border highlighting. Reset whenever the step
  // changes so a fresh step never starts in an "errored" state.
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [step1Form, setStep1Form] = useState<Step1FormData>(emptyStep1Form);
  const [billingSameAsAbove, setBillingSameAsAbove] = useState(false);
  const [step1Expanded, setStep1Expanded] = useState({
    basic: true,
    company: true,
    billing: true
  });

  const { isTeamMember } = useAuth();
  const { data: vendor, isLoading: isLoadingVendor, error: vendorError, refetch: refetchVendor } = useGetVendorMeQuery(undefined, { skip: isTeamMember });
  const { data: existingQuestions } = useGetUseCaseQuestionsQuery();
  const { data: existingReviewQuestions } = useGetReviewQuestionsQuery();
  const { data: rewardSettings } = useGetRewardSettingsQuery();
  const [updateUseCaseQuestions] = useUpdateUseCaseQuestionsMutation();
  const [updateReviewQuestions] = useUpdateReviewQuestionsMutation();
  const [updateVendor, { isLoading: isUpdatingVendor, error: updateVendorError, reset: resetUpdateVendorError }] = useUpdateVendorMutation();
  const [updateRewardSettings] = useUpdateRewardSettingsMutation();

  const [rewardCurrency, setRewardCurrency] = useState<'EUR' | 'USD' | 'GBP'>('EUR');
  const [rewardConfigs, setRewardConfigs] = useState<RewardConfig[]>([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountConfigId, setDiscountConfigId] = useState<string | null>(null);
  const [discountStage, setDiscountStage] = useState<'single' | 'lead' | 'deal'>('single');
  const [showReferralConfigModal, setShowReferralConfigModal] = useState(false);
  const [referralConfigModalId, setReferralConfigModalId] = useState<string | null>(null);

  // True once a saved draft has been hydrated into state. Prevents API useEffects
  // from overwriting draft data when the server responds after hydration.
  const draftLoaded = useRef(false);

  // Hydrate state from the draft on mount (runs once, before any API response).
  useEffect(() => {
    const draft = loadDraft();
    if (!draft) return;
    draftLoaded.current = true;
    setCurrentStep(draft.currentStep);
    setStep1Form(draft.step1Form);
    setBillingSameAsAbove(draft.billingSameAsAbove);
    setStep1Expanded(draft.step1Expanded);
    setUseCaseQuestions(draft.useCaseQuestions);
    setReviewQuestions(draft.reviewQuestions);
    setProducts(draft.products);
    setNoProducts(draft.noProducts);
    setRewardCurrency(draft.rewardCurrency);
    setRewardConfigs(draft.rewardConfigs);
  }, []);

  useEffect(() => {
    if (draftLoaded.current) return;
    if (rewardSettings) {
      setRewardCurrency((rewardSettings.currency as 'EUR' | 'USD' | 'GBP') || 'EUR');
      setRewardConfigs(rewardSettings.reward_configs || []);
    }
  }, [rewardSettings]);

  useEffect(() => {
    if (draftLoaded.current) return;
    if (vendor) {
      setStep1Form(getStep1FormFromVendor(vendor));
    }
  }, [vendor]);

  useEffect(() => {
    if (billingSameAsAbove) {
      setStep1Form((prev) => ({
        ...prev,
        billingContact: {
          ...prev.billingContact,
          firstName: prev.first_name,
          lastName: prev.last_name,
          email: prev.email,
          street: prev.street,
          zip: prev.zip,
          city: prev.city
        }
      }));
    }
  }, [billingSameAsAbove, step1Form.first_name, step1Form.last_name, step1Form.email, step1Form.street, step1Form.zip, step1Form.city]);

  const handleStep1Change = <K extends keyof Step1FormData>(field: K, value: Step1FormData[K]) => {
    setStep1Form((prev) => ({ ...prev, [field]: value }));
  };

  const handleBillingContactChange = <K extends keyof Step1FormData['billingContact']>(field: K, value: string) => {
    setStep1Form((prev) => ({
      ...prev,
      billingContact: { ...prev.billingContact, [field]: value }
    }));
  };

  const [useCaseQuestions, setUseCaseQuestions] = useState<UseCaseQuestion[]>([]);
  const hasSeededDefaultQuestions = useRef(false);
  const [reviewQuestions, setReviewQuestions] = useState<UseCaseQuestion[]>([]);
  const hasSeededDefaultReviewQuestions = useRef(false);
  const [products, setProducts] = useState<Array<{ name: string; description: string }>>([]);
  const [noProducts, setNoProducts] = useState(false);

  useEffect(() => {
    if (draftLoaded.current) return;
    if (vendor?.pageInfo?.products && vendor.pageInfo.products.length > 0) {
      setProducts(vendor.pageInfo.products.map((p) => ({ name: p.name, description: p.description || '' })));
    }
  }, [vendor?.pageInfo?.products]);

  // Load existing questions when API responds; if vendor has none yet, show default template questions once (editable/deletable)
  useEffect(() => {
    if (draftLoaded.current) return;
    if (existingQuestions === undefined) return; // still loading
    if (existingQuestions.length > 0) {
      hasSeededDefaultQuestions.current = false; // reset so future empty response can seed again if needed
      setUseCaseQuestions(
        existingQuestions.map((q) => ({
          id: q.id,
          question: q.question,
          choices: toChoices(q.choices || []),
          answerType: (q.answer_type || 'many_selected') as AnswerType,
        }))
      );
    } else if (!hasSeededDefaultQuestions.current) {
      hasSeededDefaultQuestions.current = true;
      setUseCaseQuestions([...DEFAULT_USE_CASE_QUESTIONS]);
    }
  }, [existingQuestions]);

  useEffect(() => {
    if (draftLoaded.current) return;
    if (existingReviewQuestions === undefined) return;
    if (existingReviewQuestions.length > 0) {
      hasSeededDefaultReviewQuestions.current = false;
      setReviewQuestions(
        existingReviewQuestions.map((q) => ({
          id: q.id,
          question: q.question,
          choices: toChoices(q.choices || []),
          answerType: (q.answer_type || 'one_selected') as AnswerType,
        }))
      );
    } else if (!hasSeededDefaultReviewQuestions.current) {
      hasSeededDefaultReviewQuestions.current = true;
      setReviewQuestions(DEFAULT_REVIEW_QUESTIONS.map(q => ({ ...q, id: Date.now().toString() + q.id })));
    }
  }, [existingReviewQuestions]);

  // Persist state to localStorage on every change, debounced by 500 ms.
  // Guard: only save after data is initialized — either a draft was loaded on mount,
  // or the API has responded with questions (both undefined → still loading).
  // This prevents saving the empty initial state before the API responds, which
  // would corrupt the draft and cause validation errors on the next refresh.
  useEffect(() => {
    const ready = draftLoaded.current || (existingQuestions !== undefined && existingReviewQuestions !== undefined);
    if (!ready) return;

    const timer = setTimeout(() => {
      saveDraft({
        version: DRAFT_VERSION,
        savedAt: new Date().toISOString(),
        currentStep,
        step1Form,
        billingSameAsAbove,
        step1Expanded,
        useCaseQuestions,
        reviewQuestions,
        products,
        noProducts,
        rewardCurrency,
        rewardConfigs,
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [currentStep, step1Form, billingSameAsAbove, step1Expanded, useCaseQuestions, reviewQuestions, products, noProducts, rewardCurrency, rewardConfigs, existingQuestions, existingReviewQuestions]);

  const addQuestion = () => {
    const newQuestion: UseCaseQuestion = {
      id: Date.now().toString(),
      question: '',
      choices: [],
      answerType: 'many_selected',
    };
    setUseCaseQuestions(prev => [...prev, newQuestion]);
  };

  const updateAnswerType = (questionId: string, answerType: AnswerType) => {
    setUseCaseQuestions(prev =>
      prev.map(q => q.id === questionId ? { ...q, answerType } : q)
    );
  };

  const updateQuestion = (questionId: string, newText: string) => {
    setUseCaseQuestions(prev =>
      prev.map(q => q.id === questionId ? { ...q, question: newText } : q)
    );
  };

  const deleteQuestion = (questionId: string) => {
    setUseCaseQuestions(prev => prev.filter(q => q.id !== questionId));
  };

  const addChoice = (questionId: string) => {
    setUseCaseQuestions(prev =>
      prev.map(q => q.id === questionId ? { ...q, choices: [...q.choices, { id: crypto.randomUUID(), text: '' }] } : q)
    );
  };

  const updateChoice = (questionId: string, choiceId: string, newValue: string) => {
    setUseCaseQuestions(prev =>
      prev.map(q => {
        if (q.id !== questionId) return q;
        return { ...q, choices: q.choices.map(c => c.id === choiceId ? { ...c, text: newValue } : c) };
      })
    );
  };

  const deleteChoice = (questionId: string, choiceId: string) => {
    setUseCaseQuestions(prev =>
      prev.map(q => {
        if (q.id !== questionId) return q;
        return { ...q, choices: q.choices.filter(c => c.id !== choiceId) };
      })
    );
  };

  const handleUseCaseDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId.startsWith('choices-')) {
      const questionId = source.droppableId.slice('choices-'.length);
      setUseCaseQuestions(prev =>
        prev.map(q => {
          if (q.id !== questionId) return q;
          const choices = Array.from(q.choices);
          const [moved] = choices.splice(source.index, 1);
          choices.splice(destination.index, 0, moved);
          return { ...q, choices };
        })
      );
    } else {
      const reordered = Array.from(useCaseQuestions);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      setUseCaseQuestions(reordered);
    }
  };

  const handleReviewDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId.startsWith('review-choices-')) {
      const questionId = source.droppableId.slice('review-choices-'.length);
      setReviewQuestions(prev =>
        prev.map(q => {
          if (q.id !== questionId) return q;
          const choices = Array.from(q.choices);
          const [moved] = choices.splice(source.index, 1);
          choices.splice(destination.index, 0, moved);
          return { ...q, choices };
        })
      );
    } else {
      const reordered = Array.from(reviewQuestions);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      setReviewQuestions(reordered);
    }
  };

  const addProduct = () => {
    setProducts((prev) => [...prev, { name: '', description: '' }]);
  };
  const removeProduct = (index: number) => {
    setProducts((prev) => prev.filter((_, i) => i !== index));
  };
  const updateProduct = (index: number, field: 'name' | 'description', value: string) => {
    setProducts((prev) =>
      prev.map((p, i) => (i === index ? { ...p, [field]: value } : p))
    );
  };

  const canProceedFromStep = (step: OnboardingStep): boolean => {
    switch (step) {
      case 1: {
        const bc = step1Form.billingContact;
        const billingOk = !!(bc.phone?.trim()) && !!(bc.country?.trim());
        return !!(
          step1Form.first_name.trim().length >= 2 &&
          step1Form.last_name.trim().length >= 2 &&
          step1Form.street.trim().length >= 2 &&
          step1Form.country.trim() !== '' &&
          step1Form.website.trim() !== '' &&
          (!step1Form.vendorLinkedin.trim() || isValidLinkedInPersonUrl(step1Form.vendorLinkedin)) &&
          (!step1Form.companyLinkedin.trim() || isValidLinkedInCompanyUrl(step1Form.companyLinkedin)) &&
          billingOk &&
          vendor?.email
        );
      }
      case 2:
        return noProducts || products.some(p => p.name.trim() !== '');
      case 3: {
        const productNames = products.map(p => p.name.trim()).filter(Boolean);
        return reviewQuestions.some(q => isQuestionSavable(q, productNames));
      }
      case 4: {
        const productNames = products.map(p => p.name.trim()).filter(Boolean);
        return useCaseQuestions.some(q => isQuestionSavable(q, productNames));
      }
      case 5:
      case 6:
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (!canProceedFromStep(currentStep)) {
      setValidationAttempted(true);
      return;
    }
    setValidationAttempted(false);
    if (currentStep < 6) {
      setCurrentStep((prev) => (prev + 1) as OnboardingStep);
    }
  };

  const handleCompleteLater = () => {
    sessionStorage.setItem('onboardingDeferred', 'true');
    navigate('/dashboard');
  };

  const handleBack = () => {
    setValidationAttempted(false);
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as OnboardingStep);
    }
  };

  const handleRewardConfigAmountChange = (configId: string, amount: number) => {
    setRewardConfigs(prev =>
      prev.map(c => (c.id === configId ? { ...c, amount } : c))
    );
  };

  const handleRewardMethodToggle = (configId: string, method: string) => {
    setRewardConfigs(prev =>
      prev.map(c => (c.id === configId ? { ...c, reward_method: method } : c))
    );
  };

  const handleRewardTriggerChange = (configId: string, trigger: string) => {
    setRewardConfigs(prev =>
      prev.map(c => (c.id === configId ? { ...c, reward_policy_trigger: trigger } : c))
    );
  };

  const handleRewardStageAmountChange = (configId: string, stage: 'lead' | 'deal', amount: number) => {
    setRewardConfigs(prev =>
      prev.map(c =>
        c.id === configId
          ? stage === 'lead'
            ? { ...c, lead_submission_amount: amount }
            : { ...c, deal_won_amount: amount }
          : c
      )
    );
  };

  const handleStageMethodChange = (configId: string, stage: 'lead' | 'deal', method: string) => {
    setRewardConfigs(prev =>
      prev.map(c =>
        c.id === configId
          ? stage === 'lead'
            ? { ...c, lead_submission_reward_method: method }
            : { ...c, deal_won_reward_method: method }
          : c
      )
    );
  };

  const handleReferralDiscountChange = (
    configId: string,
    stage: 'single' | 'lead' | 'deal',
    field: 'discount_type' | 'discount_value' | 'discount_product_name',
    value: string | number | null
  ) => {
    setRewardConfigs(prev =>
      prev.map(c => {
        if (c.id !== configId) return c;
        if (stage === 'lead') return { ...c, [`lead_submission_${field}`]: value };
        if (stage === 'deal') return { ...c, [`deal_won_${field}`]: value };
        return { ...c, [field]: value };
      })
    );
  };

  const handleDiscountSave = (discount: DiscountReward) => {
    if (!discountConfigId) return;
    setRewardConfigs(prev =>
      prev.map(c => {
        if (c.id !== discountConfigId) return c;
        if (discountStage === 'lead') {
          return {
            ...c,
            lead_submission_discount_name: buildDiscountRewardName(discount),
            lead_submission_discount_type: discount.discountType,
            lead_submission_discount_value: discount.discountValue,
            lead_submission_discount_product_name: discount.productName?.trim() || null,
          };
        }
        if (discountStage === 'deal') {
          return {
            ...c,
            deal_won_discount_name: buildDiscountRewardName(discount),
            deal_won_discount_type: discount.discountType,
            deal_won_discount_value: discount.discountValue,
            deal_won_discount_product_name: discount.productName?.trim() || null,
          };
        }
        return {
          ...c,
          discount_name: buildDiscountRewardName(discount),
          discount_description: null,
          discount_type: discount.discountType,
          discount_value: discount.discountValue,
          discount_validity_days: null,
          discount_min_purchase: null,
          discount_max_discount: null,
          discount_terms: null,
          discount_product_name: discount.productName?.trim() || null,
        };
      })
    );
    setShowDiscountModal(false);
    setDiscountConfigId(null);
    setDiscountStage('single');
  };

  const handleComplete = async () => {
    resetUpdateVendorError();
    setIsProcessing(true);
    try {
      const productNames = products.map(p => p.name.trim()).filter(Boolean);
      const questionsToSave = useCaseQuestions
        .filter(q => isQuestionSavable(q, productNames))
        .map((q, index) => ({
          question: q.question.trim(),
          choices: q.answerType === 'free_text' ? []
            : q.answerType === 'numeral' ? Array.from({ length: 10 }, (_, i) => String(i + 1))
            : q.answerType === 'product_select' ? productNames
            : q.choices.filter(c => c.text.trim() !== '').map(c => c.text),
          order: index,
          question_type: 'USE_CASE',
          answer_type: q.answerType,
        }));

      if (questionsToSave.length > 0) {
        await updateUseCaseQuestions({ questions: questionsToSave }).unwrap();
      }

      const reviewQuestionsToSave = reviewQuestions
        .filter(q => isQuestionSavable(q, productNames))
        .map((q, index) => ({
          question: q.question.trim(),
          choices: q.answerType === 'free_text' ? []
            : q.answerType === 'numeral' ? Array.from({ length: 10 }, (_, i) => String(i + 1))
            : q.answerType === 'product_select' ? productNames
            : q.choices.filter(c => c.text.trim() !== '').map(c => c.text),
          order: index,
          question_type: 'REVIEW',
          answer_type: q.answerType,
        }));

      if (reviewQuestionsToSave.length > 0) {
        await updateReviewQuestions({ questions: reviewQuestionsToSave }).unwrap();
      }

      if (vendor?.email) {
        const mainAddress = formatAddress(step1Form.street, step1Form.zip, step1Form.city, step1Form.country);
        const bc = step1Form.billingContact;
        const bcAddress = formatAddress(bc.street, bc.zip, bc.city, bc.country);
        const billingContactPayload = bc.phone && bc.country
          ? {
              firstName: bc.firstName ?? '',
              lastName: bc.lastName ?? '',
              email: bc.email ?? '',
              address: bcAddress,
              phone: bc.phone,
              streetAddress: bc.street.trim() || undefined,
              zipCode: bc.zip.trim() || undefined,
              city: bc.city.trim() || undefined,
              country: bc.country.trim() || undefined
            }
          : undefined;

        const pageInfoProducts = products.filter((p) => p.name.trim() !== '').map((p) => ({ name: p.name.trim(), description: p.description.trim() || '' }));
        const pageInfo = vendor.pageInfo
          ? { ...vendor.pageInfo, companyUrl: step1Form.website.trim() || vendor.pageInfo.companyUrl || undefined, products: pageInfoProducts.length > 0 ? pageInfoProducts : vendor.pageInfo.products }
          : step1Form.website.trim()
            ? { companyUrl: step1Form.website.trim(), products: pageInfoProducts.length > 0 ? pageInfoProducts : undefined }
            : pageInfoProducts.length > 0
              ? { products: pageInfoProducts }
              : undefined;
        await updateVendor({
          email: vendor.email,
          firstName: step1Form.first_name.trim(),
          lastName: step1Form.last_name.trim(),
          jobTitle: step1Form.job_title.trim() || undefined,
          address: mainAddress || undefined,
          streetAddress: step1Form.street.trim() || undefined,
          zipCode: step1Form.zip.trim() || undefined,
          city: step1Form.city.trim() || undefined,
          country: step1Form.country.trim() || undefined,
          phone: step1Form.phone.trim() || undefined,
          companyName: step1Form.companyName.trim() || undefined,
          businessIdentificationNumber: step1Form.businessIdentificationNumber.trim() || undefined,
          vendorLinkedin: step1Form.vendorLinkedin.trim() || undefined,
          companyLinkedin: step1Form.companyLinkedin.trim() || undefined,
          description: step1Form.description.trim() || undefined,
          logo: step1Form.logo.trim() || undefined,
          avatar: step1Form.avatar.trim() || undefined,
          pageInfo,
          billingContact: billingContactPayload,
          onboardingCompleted: true
        }).unwrap();
      }

      if (rewardConfigs.length > 0) {
        await updateRewardSettings({
          currency: rewardCurrency,
          reward_configs: rewardConfigs,
        }).unwrap();
      }

      const companyInfoForStorage = {
        companyName: step1Form.companyName,
        industry: '',
        website: step1Form.website,
        logo: step1Form.logo,
        contactName: `${step1Form.billingContact.firstName} ${step1Form.billingContact.lastName}`.trim(),
        contactEmail: step1Form.billingContact.email,
        contactPhone: step1Form.billingContact.phone,
        address: formatAddress(step1Form.billingContact.street, step1Form.billingContact.zip, step1Form.billingContact.city, step1Form.billingContact.country)
      };
      const onboardingData = {
        companyInfo: companyInfoForStorage,
        useCaseQuestions: questionsToSave,
        completedAt: new Date().toISOString()
      };
      localStorage.setItem('onboardingData', JSON.stringify(onboardingData));
      clearDraft();
      sessionStorage.removeItem('onboardingDeferred');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save onboarding:', error);
      setIsProcessing(false);
    }
  };

  const hasVendorData = !!(vendor?.companyName || vendor?.logo || vendor?.pageInfo?.companyUrl || vendor?.billingContact || vendor?.adminContact);
  const inputBase = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent disabled:bg-gray-100';
  const inputInvalid = 'w-full px-4 py-2 border-2 border-red-500 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:bg-gray-100';
  // Required-field red border only kicks in after a failed Continue click, and
  // only while the field is actually still empty — it clears itself the moment
  // the user types, since this is re-evaluated on every render.
  const requiredFieldClass = (value: string, minLen = 1) =>
    validationAttempted && value.trim().length < minLen ? inputInvalid : inputBase;

  const renderStep1 = () => (
    <div>
      <div className="mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>{t('onboarding.step1.whatThisDoesLabel')}</strong> {t('onboarding.step1.whatThisDoesText')}
          </p>
        </div>
        {hasVendorData && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">
              {t('onboarding.step1.prefilledInfo')}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Basic information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setStep1Expanded((p) => ({ ...p, basic: !p.basic }))}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50/50 transition-colors border-b border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <User className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">{t('onboarding.step1.basicInfo')}</h3>
                <p className="text-sm text-gray-500">{t('onboarding.step1.basicInfoDesc')}</p>
              </div>
            </div>
            {step1Expanded.basic ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {step1Expanded.basic && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.firstName')} <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={step1Form.first_name}
                onChange={(e) => handleStep1Change('first_name', e.target.value)}
                className={requiredFieldClass(step1Form.first_name, 2)}
                placeholder={t('onboarding.step1.firstNamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.lastName')} <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={step1Form.last_name}
                onChange={(e) => handleStep1Change('last_name', e.target.value)}
                className={requiredFieldClass(step1Form.last_name, 2)}
                placeholder={t('onboarding.step1.lastNamePlaceholder')}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.jobTitle')}</label>
              <input
                type="text"
                value={step1Form.job_title}
                onChange={(e) => handleStep1Change('job_title', e.target.value)}
                className={inputBase}
                placeholder={t('common.jobTitle')}
                maxLength={100}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.streetAddress')} <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={step1Form.street}
                onChange={(e) => handleStep1Change('street', e.target.value)}
                className={requiredFieldClass(step1Form.street, 2)}
                placeholder={t('onboarding.step1.streetAddressPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.zip')}</label>
              <input
                type="text"
                value={step1Form.zip}
                onChange={(e) => handleStep1Change('zip', e.target.value)}
                className={inputBase}
                placeholder={t('onboarding.step1.zipPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.city')}</label>
              <input
                type="text"
                value={step1Form.city}
                onChange={(e) => handleStep1Change('city', e.target.value)}
                className={inputBase}
                placeholder={t('onboarding.step1.cityPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.country')} <span className="text-red-500">*</span></label>
              <CountrySelect
                value={step1Form.country}
                onChange={(v) => handleStep1Change('country', v)}
                placeholder={t('onboarding.step1.selectCountry')}
                error={validationAttempted && !step1Form.country.trim()}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.email')}</label>
              <input
                type="email"
                value={vendor?.email ?? step1Form.email}
                readOnly
                disabled
                className={inputBase}
              />
              <p className="text-xs text-gray-500 mt-1">{t('onboarding.step1.emailCannotBeChanged')}</p>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.vendorLinkedin')}</label>
              <input
                type="url"
                value={step1Form.vendorLinkedin}
                onChange={(e) => handleStep1Change('vendorLinkedin', e.target.value)}
                className={inputBase}
                placeholder={t('onboarding.step1.vendorLinkedinPlaceholder')}
              />
              {step1Form.vendorLinkedin.trim() && !isValidLinkedInPersonUrl(step1Form.vendorLinkedin) && (
                <p className="text-xs text-red-500 mt-1">{t('onboarding.step1.vendorLinkedinInvalid')}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.phone')}</label>
              <PhoneInput
                value={step1Form.phone}
                onChange={(v) => handleStep1Change('phone', v)}
              />
            </div>
          </div>
          )}
        </div>

        {/* Company */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setStep1Expanded((p) => ({ ...p, company: !p.company }))}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50/50 transition-colors border-b border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">{t('onboarding.step1.companySection')}</h3>
                <p className="text-sm text-gray-500">{t('onboarding.step1.companySectionDesc')}</p>
              </div>
            </div>
            {step1Expanded.company ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {step1Expanded.company && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.companyName')}</label>
              <input
                type="text"
                value={step1Form.companyName}
                onChange={(e) => handleStep1Change('companyName', e.target.value)}
                className={inputBase}
                placeholder={t('onboarding.step1.companyNamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.businessId')}</label>
              <input
                type="text"
                value={step1Form.businessIdentificationNumber}
                onChange={(e) => handleStep1Change('businessIdentificationNumber', e.target.value)}
                className={inputBase}
                placeholder={t('onboarding.step1.businessIdPlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.companyLinkedin')}</label>
              <input
                type="url"
                value={step1Form.companyLinkedin}
                onChange={(e) => handleStep1Change('companyLinkedin', e.target.value)}
                className={inputBase}
                placeholder={t('onboarding.step1.companyLinkedinPlaceholder')}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.descriptionLabel')}</label>
              <textarea
                value={step1Form.description}
                onChange={(e) => handleStep1Change('description', e.target.value)}
                className={`${inputBase} min-h-[100px]`}
                placeholder={t('onboarding.step1.descriptionPlaceholder')}
                rows={4}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.website')} <span className="text-red-500">*</span></label>
              <input
                type="url"
                value={step1Form.website}
                onChange={(e) => {
                  const v = e.target.value;
                  handleStep1Change('website', v);
                  if (v?.trim() && !step1Form.logo?.trim()) {
                    const iconUrl = getIconHorseUrl(v);
                    if (iconUrl) {
                      const img = new Image();
                      img.onload = () => handleStep1Change('logo', iconUrl);
                      img.src = iconUrl;
                    }
                  }
                }}
                onBlur={(e) => {
                  const v = e.target.value?.trim();
                  if (v && !step1Form.logo?.trim()) {
                    const iconUrl = getIconHorseUrl(v);
                    if (iconUrl) {
                      const img = new Image();
                      img.onload = () => handleStep1Change('logo', iconUrl);
                      img.src = iconUrl;
                    }
                  }
                }}
                className={requiredFieldClass(step1Form.website)}
                placeholder={t('onboarding.step1.websitePlaceholder')}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.companyLogo')}</label>
              <ImageUploadWithCrop
                value={step1Form.logo}
                onChange={(v) => handleStep1Change('logo', v)}
                onUpload={(blob) => uploadFile(blob, 'vendors/logos')}
                aspect={16 / 9}
                placeholder={t('onboarding.step1.uploadLogo')}
                allowUrlInput
              />
            </div>
          </div>
          )}
        </div>

        {/* Media section removed — logo moved to Company section; avatar replaced by initials */}
        {/* <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          ...avatar upload removed...
        </div> */}

        {/* Billing contact */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setStep1Expanded((p) => ({ ...p, billing: !p.billing }))}
            className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50/50 transition-colors border-b border-gray-200"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">{t('onboarding.step1.billingContact')}</h3>
                <p className="text-sm text-gray-500">{t('onboarding.step1.billingContactDesc')}</p>
              </div>
            </div>
            {step1Expanded.billing ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </button>
          {step1Expanded.billing && (
          <div className="p-6 space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={billingSameAsAbove}
                onChange={(e) => setBillingSameAsAbove(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-[#4339A0] focus:ring-[#4339A0]"
              />
              <span className="text-sm font-medium text-gray-700">{t('onboarding.step1.sameAsAbove')}</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.firstName')}</label>
                <input
                  type="text"
                  value={step1Form.billingContact.firstName}
                  onChange={(e) => handleBillingContactChange('firstName', e.target.value)}
                  className={inputBase}
                  disabled={billingSameAsAbove}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.lastName')}</label>
                <input
                  type="text"
                  value={step1Form.billingContact.lastName}
                  onChange={(e) => handleBillingContactChange('lastName', e.target.value)}
                  className={inputBase}
                  disabled={billingSameAsAbove}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.email')}</label>
                <input
                  type="email"
                  value={step1Form.billingContact.email}
                  onChange={(e) => handleBillingContactChange('email', e.target.value)}
                  className={inputBase}
                  disabled={billingSameAsAbove}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.phone')} <span className="text-red-500">*</span></label>
                <PhoneInput
                  value={step1Form.billingContact.phone}
                  onChange={(v) => handleBillingContactChange('phone', v)}
                  error={validationAttempted && !step1Form.billingContact.phone?.trim()}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('common.street')}</label>
                <input
                  type="text"
                  value={step1Form.billingContact.street}
                  onChange={(e) => handleBillingContactChange('street', e.target.value)}
                  className={inputBase}
                  placeholder={t('onboarding.step1.streetAddressPlaceholder')}
                  disabled={billingSameAsAbove}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.zip')}</label>
                <input
                  type="text"
                  value={step1Form.billingContact.zip}
                  onChange={(e) => handleBillingContactChange('zip', e.target.value)}
                  className={inputBase}
                  placeholder={t('onboarding.step1.zipPlaceholder')}
                  disabled={billingSameAsAbove}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.city')}</label>
                <input
                  type="text"
                  value={step1Form.billingContact.city}
                  onChange={(e) => handleBillingContactChange('city', e.target.value)}
                  className={inputBase}
                  placeholder={t('onboarding.step1.cityPlaceholder')}
                  disabled={billingSameAsAbove}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step1.country')} <span className="text-red-500">*</span></label>
                <CountrySelect
                  value={step1Form.billingContact.country}
                  onChange={(v) => handleBillingContactChange('country', v)}
                  placeholder={t('onboarding.step1.selectCountry')}
                  error={validationAttempted && !step1Form.billingContact.country?.trim()}
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2Products = () => (
    <div>
      <div className="mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>{t('onboarding.step2.infoLabel')}</strong>{t('onboarding.step2.infoText')}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="mb-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={noProducts}
              onChange={(e) => setNoProducts(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 text-[#4339A0] focus:ring-[#4339A0]"
            />
            <span className="text-sm text-gray-700">{t('onboarding.step2.noProductsOption')}</span>
          </label>
        </div>

        {!noProducts && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('onboarding.step2.productsSolutions')}</h3>
              <button
                type="button"
                onClick={addProduct}
                className="flex items-center space-x-2 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>{t('onboarding.step2.addProduct')}</span>
              </button>
            </div>
            <div className="space-y-4">
              {products.length === 0 ? (
                <div className="text-center py-6 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
                  <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p>{t('onboarding.step2.noProductsYet')}</p>
                  <p className="text-xs text-amber-600 mt-1 font-medium">{t('onboarding.step2.atLeastOneRequired')}</p>
                </div>
              ) : (
                products.map((product, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="text-sm font-medium text-gray-700">{t('onboarding.step2.productLabel', { index: index + 1 })}</h4>
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('onboarding.step2.productName')}</label>
                        <input
                          type="text"
                          value={product.name}
                          onChange={(e) => updateProduct(index, 'name', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                          placeholder={t('onboarding.step2.productNamePlaceholder')}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('onboarding.step2.productDescription')}</label>
                        <textarea
                          value={product.description}
                          onChange={(e) => updateProduct(index, 'description', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent min-h-[80px]"
                          placeholder={t('onboarding.step2.productDescriptionPlaceholder')}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {noProducts && (
          <div className="py-4 text-sm text-gray-500 italic">
            {t('onboarding.step2.canAddLater')}
          </div>
        )}
      </div>
    </div>
  );

  const restoreDefaultQuestions = () => {
    const productTexts = products.filter(p => p.name.trim()).map(p => p.name.trim());
    const defaults = DEFAULT_USE_CASE_QUESTIONS.map(q => {
      if (q.id === 'default-2' && productTexts.length > 0) {
        return { ...q, choices: toChoices(productTexts) };
      }
      return { ...q };
    });
    setUseCaseQuestions(defaults.map(q => ({ ...q, id: Date.now().toString() + q.id })));
  };

  const renderStep3 = () => (
    <div>
      <div className="mb-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-2">
          <p className="text-sm text-orange-800">
            <strong>{t('onboarding.step3.actionRequiredLabel')}</strong>{t('onboarding.step3.actionRequiredText')}
          </p>
          <p className="text-sm text-orange-700">
            {t('onboarding.step3.formatsText')}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <DragDropContext onDragEnd={handleUseCaseDragEnd}>
          <Droppable droppableId="onboarding-usecase-questions" type="QUESTION">
            {(droppableProvided) => (
              <div
                className="space-y-6"
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
          {useCaseQuestions.map((q, qIdx) => (
            <Draggable key={q.id} draggableId={q.id} index={qIdx}>
              {(draggableProvided, snapshot) => (
            <div
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              className={`p-6 rounded-lg border-2 border-[#4339A0] bg-[#f5f3ff] ${snapshot.isDragging ? 'shadow-lg' : ''}`}
            >
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div
                    {...draggableProvided.dragHandleProps}
                    className="mt-2 p-1 text-[#4339A0]/40 hover:text-[#4339A0] cursor-grab active:cursor-grabbing rounded transition-colors"
                    title={t('onboarding.step3.dragToReorder')}
                  >
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    value={q.question}
                    onChange={(e) => updateQuestion(q.id, e.target.value)}
                    placeholder={t('onboarding.step3.enterQuestion')}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                    maxLength={500}
                  />
                  <button
                    onClick={() => deleteQuestion(q.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title={t('onboarding.step3.deleteQuestion')}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>

                <div className="ml-0">
                  <label className="text-sm font-medium text-gray-700 block mb-1">{t('onboarding.step3.answerType')}</label>
                  <select
                    value={q.answerType}
                    onChange={(e) => updateAnswerType(q.id, e.target.value as AnswerType)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                  >
                    <option value="one_selected">{t('onboarding.step3.singleChoice')}</option>
                    <option value="many_selected">{t('onboarding.step3.multipleChoice')}</option>
                    <option value="free_text">{t('onboarding.step3.freeText')}</option>
                    <option value="numeral">{t('onboarding.step3.numeral')}</option>
                    <option value="product_select">{t('onboarding.step3.productSelect')}</option>
                  </select>
                </div>

                {(q.answerType === 'one_selected' || q.answerType === 'many_selected') && (
                  <div className="ml-0 space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700">
                        {t('onboarding.step3.choices')} <span className="text-red-500">*</span>{t('onboarding.step3.choicesMinRequired')}
                      </label>
                      <button
                        onClick={() => addChoice(q.id)}
                        className="text-sm text-[#4339A0] hover:text-[#3d2f8f] font-medium flex items-center space-x-1"
                      >
                        <Plus className="w-4 h-4" />
                        <span>{t('onboarding.step3.addChoice')}</span>
                      </button>
                    </div>

                    <Droppable droppableId={`choices-${q.id}`} type="CHOICE">
                      {(choicesProvided) => (
                        <div
                          ref={choicesProvided.innerRef}
                          {...choicesProvided.droppableProps}
                          className="space-y-2"
                        >
                          {q.choices.map((choice, choiceIndex) => (
                            <Draggable key={choice.id} draggableId={choice.id} index={choiceIndex}>
                              {(choiceDraggable, choiceSnapshot) => (
                                <div
                                  ref={choiceDraggable.innerRef}
                                  {...choiceDraggable.draggableProps}
                                  className={`flex items-center space-x-2 ${choiceSnapshot.isDragging ? 'shadow-md bg-white rounded-lg' : ''}`}
                                >
                                  <div
                                    {...choiceDraggable.dragHandleProps}
                                    className="p-1 text-[#4339A0]/40 hover:text-[#4339A0] cursor-grab active:cursor-grabbing rounded transition-colors"
                                    title={t('onboarding.step3.dragToReorder')}
                                  >
                                    <GripVertical className="w-4 h-4" />
                                  </div>
                                  <input
                                    type="text"
                                    value={choice.text}
                                    onChange={(e) => updateChoice(q.id, choice.id, e.target.value)}
                                    placeholder={t('onboarding.step3.choicePlaceholder', { index: choiceIndex + 1 })}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
                                    maxLength={200}
                                  />
                                  <button
                                    onClick={() => deleteChoice(q.id, choice.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title={t('onboarding.step3.deleteChoice')}
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {choicesProvided.placeholder}
                        </div>
                      )}
                    </Droppable>

                    {q.choices.length < 2 && (
                      <p className="text-xs text-red-600">
                        {t('onboarding.step3.minChoicesError')}
                      </p>
                    )}
                  </div>
                )}

                {q.answerType === 'numeral' && (
                  <div className="ml-0">
                    <label className="text-sm font-medium text-gray-700 block mb-2">{t('onboarding.step3.scalePreview')}</label>
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(n => (
                        <div key={n} className="w-10 h-10 rounded-lg border-2 border-gray-200 flex items-center justify-center font-semibold text-sm text-gray-500">{n}</div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{t('onboarding.step3.promotersSelectNumber')}</p>
                  </div>
                )}

                {q.answerType === 'product_select' && (
                  <div className="ml-0">
                    <label className="text-sm font-medium text-gray-700 block mb-2">{t('onboarding.step3.products')}</label>
                    {(() => {
                      const prods = products.filter(p => p.name.trim());
                      if (prods.length === 0) return (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
                          {t('onboarding.step3.noProductsYet')}
                        </div>
                      );
                      return (
                        <div className="flex flex-wrap gap-2">
                          {prods.map(p => (
                            <span key={p.name} className="px-3 py-1.5 bg-[#4339A0]/10 border border-[#4339A0]/20 rounded-lg text-sm text-[#4339A0]">{p.name}</span>
                          ))}
                        </div>
                      );
                    })()}
                    <p className="text-xs text-gray-400 mt-2">{t('onboarding.step3.promotersSelectProducts')}</p>
                  </div>
                )}
              </div>
            </div>
              )}
            </Draggable>
          ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <div className="mt-6 flex items-center gap-3">
          <button
            onClick={addQuestion}
            className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-[#4339A0] hover:text-[#4339A0] transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>{t('onboarding.step3.addNewQuestion')}</span>
          </button>
          <button
            type="button"
            onClick={restoreDefaultQuestions}
            className="flex items-center space-x-2 px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-colors text-sm whitespace-nowrap"
            title={t('onboarding.step3.restoreDefaultsTooltip')}
          >
            <span>{t('onboarding.step3.restoreDefaults')}</span>
          </button>
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>{t('onboarding.step3.tipLabel')}</strong>{t('onboarding.step3.tipText')}
          </p>
        </div>
      </div>
    </div>
  );

  const addReviewQuestion = () => {
    setReviewQuestions(prev => [...prev, { id: Date.now().toString(), question: '', choices: [], answerType: 'one_selected' }]);
  };
  const updateReviewQuestion = (id: string, text: string) => {
    setReviewQuestions(prev => prev.map(q => q.id === id ? { ...q, question: text } : q));
  };
  const updateReviewAnswerType = (id: string, answerType: AnswerType) => {
    setReviewQuestions(prev => prev.map(q => q.id === id ? { ...q, answerType } : q));
  };
  const deleteReviewQuestion = (id: string) => {
    setReviewQuestions(prev => prev.filter(q => q.id !== id));
  };
  const addReviewChoice = (questionId: string) => {
    setReviewQuestions(prev => prev.map(q => q.id === questionId ? { ...q, choices: [...q.choices, { id: crypto.randomUUID(), text: '' }] } : q));
  };
  const updateReviewChoice = (questionId: string, choiceId: string, newValue: string) => {
    setReviewQuestions(prev => prev.map(q => {
      if (q.id !== questionId) return q;
      return { ...q, choices: q.choices.map(c => c.id === choiceId ? { ...c, text: newValue } : c) };
    }));
  };
  const deleteReviewChoice = (questionId: string, choiceId: string) => {
    setReviewQuestions(prev => prev.map(q => {
      if (q.id !== questionId) return q;
      return { ...q, choices: q.choices.filter(c => c.id !== choiceId) };
    }));
  };
  const restoreDefaultReviewQuestions = () => {
    setReviewQuestions(DEFAULT_REVIEW_QUESTIONS.map(q => ({ ...q, id: Date.now().toString() + q.id })));
  };

  const renderStep4Review = () => (
    <div>
      <div className="mb-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
          <p className="text-sm text-amber-800">
            <strong>{t('onboarding.step4Review.actionRequiredLabel')}</strong>{t('onboarding.step4Review.actionRequiredText')}
          </p>
          <p className="text-sm text-amber-700">
            {t('onboarding.step4Review.formatsText')}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{t('onboarding.step4Review.reviewQuestionsHeading')}</h3>
          <button
            type="button"
            onClick={restoreDefaultReviewQuestions}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-colors text-xs"
          >
            <Star className="w-3.5 h-3.5" />
            {t('onboarding.step4Review.restoreDefaults')}
          </button>
        </div>
        <DragDropContext onDragEnd={handleReviewDragEnd}>
          <Droppable droppableId="onboarding-review-questions" type="QUESTION">
            {(droppableProvided) => (
              <div
                className="space-y-6"
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
          {reviewQuestions.map((q, qIdx) => (
            <Draggable key={q.id} draggableId={q.id} index={qIdx}>
              {(draggableProvided, snapshot) => (
            <div
              ref={draggableProvided.innerRef}
              {...draggableProvided.draggableProps}
              className={`p-6 rounded-lg border-2 border-amber-400 bg-amber-50 ${snapshot.isDragging ? 'shadow-lg' : ''}`}
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3">
                  <div
                    {...draggableProvided.dragHandleProps}
                    className="mt-1 p-1 text-amber-400/60 hover:text-amber-500 cursor-grab active:cursor-grabbing rounded transition-colors shrink-0"
                    title={t('onboarding.step4Review.dragToReorder')}
                  >
                    <GripVertical className="w-5 h-5" />
                  </div>
                  <textarea
                    value={q.question}
                    onChange={(e) => updateReviewQuestion(q.id, e.target.value)}
                    className="flex-1 min-h-[72px] px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white resize-none"
                    placeholder={t('onboarding.step4Review.enterReviewQuestion')}
                  />
                  <button
                    type="button"
                    onClick={() => deleteReviewQuestion(q.id)}
                    className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-medium text-gray-600 whitespace-nowrap">{t('onboarding.step4Review.answerTypeLabel')}</label>
                    <select
                      value={q.answerType}
                      onChange={(e) => updateReviewAnswerType(q.id, e.target.value as AnswerType)}
                      className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:ring-2 focus:ring-amber-500 bg-white"
                    >
                      <option value="one_selected">{t('onboarding.step4Review.singleChoice')}</option>
                      <option value="many_selected">{t('onboarding.step4Review.multipleChoice')}</option>
                      <option value="free_text">{t('onboarding.step4Review.freeText')}</option>
                      <option value="numeral">{t('onboarding.step4Review.numeral')}</option>
                      <option value="product_select">{t('onboarding.step4Review.productSelect')}</option>
                    </select>
                  </div>
                </div>
                {(q.answerType === 'one_selected' || q.answerType === 'many_selected') && (
                  <div className="space-y-2 ml-2">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">{t('onboarding.step4Review.choicesRequired')} <span className="text-red-400">*</span></p>
                    <Droppable droppableId={`review-choices-${q.id}`} type="CHOICE">
                      {(choicesProvided) => (
                        <div
                          ref={choicesProvided.innerRef}
                          {...choicesProvided.droppableProps}
                          className="space-y-2"
                        >
                          {q.choices.map((choice, cIndex) => (
                            <Draggable key={choice.id} draggableId={choice.id} index={cIndex}>
                              {(choiceDraggable, choiceSnapshot) => (
                                <div
                                  ref={choiceDraggable.innerRef}
                                  {...choiceDraggable.draggableProps}
                                  className={`flex items-center gap-2 ${choiceSnapshot.isDragging ? 'shadow-md bg-white rounded-lg' : ''}`}
                                >
                                  <div
                                    {...choiceDraggable.dragHandleProps}
                                    className="p-1 text-amber-400/60 hover:text-amber-500 cursor-grab active:cursor-grabbing rounded transition-colors"
                                    title={t('onboarding.step4Review.dragToReorder')}
                                  >
                                    <GripVertical className="w-4 h-4" />
                                  </div>
                                  <input
                                    type="text"
                                    value={choice.text}
                                    onChange={(e) => updateReviewChoice(q.id, choice.id, e.target.value)}
                                    className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                                    placeholder={t('onboarding.step4Review.choicePlaceholder', { index: cIndex + 1 })}
                                  />
                                  <button type="button" onClick={() => deleteReviewChoice(q.id, choice.id)} className="text-gray-400 hover:text-red-500">
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {choicesProvided.placeholder}
                        </div>
                      )}
                    </Droppable>
                    <button
                      type="button"
                      onClick={() => addReviewChoice(q.id)}
                      className="flex items-center gap-1 text-sm text-amber-600 hover:text-amber-700 font-medium"
                    >
                      <Plus className="w-4 h-4" />
                      {t('onboarding.step4Review.addChoice')}
                    </button>
                  </div>
                )}
                {q.answerType === 'free_text' && (
                  <div className="bg-white rounded-lg px-3 py-2 text-sm text-gray-500 italic border border-gray-100">
                    {t('onboarding.step4Review.customersFreeAnswer')}
                  </div>
                )}
                {q.answerType === 'numeral' && (
                  <div className="ml-2">
                    <label className="text-xs font-medium text-gray-600 uppercase tracking-wide block mb-2">{t('onboarding.step4Review.scalePreview')}</label>
                    <div className="flex flex-wrap gap-2">
                      {Array.from({ length: 10 }, (_, i) => String(i + 1)).map(n => (
                        <div key={n} className="w-10 h-10 rounded-lg border-2 border-gray-200 flex items-center justify-center font-semibold text-sm text-gray-500">{n}</div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{t('onboarding.step4Review.customersSelectNumber')}</p>
                  </div>
                )}
                {q.answerType === 'product_select' && (
                  <div className="ml-2">
                    <label className="text-xs font-medium text-gray-600 uppercase tracking-wide block mb-2">{t('onboarding.step4Review.products')}</label>
                    {(() => {
                      const prods = products.filter(p => p.name.trim());
                      if (prods.length === 0) return (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-700">
                          {t('onboarding.step4Review.noProductsYet')}
                        </div>
                      );
                      return (
                        <div className="flex flex-wrap gap-2">
                          {prods.map(p => (
                            <span key={p.name} className="px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">{p.name}</span>
                          ))}
                        </div>
                      );
                    })()}
                    <p className="text-xs text-gray-400 mt-2">{t('onboarding.step4Review.customersSelectProducts')}</p>
                  </div>
                )}
              </div>
            </div>
              )}
            </Draggable>
          ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <button
          type="button"
          onClick={addReviewQuestion}
          className="mt-6 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-amber-300 rounded-xl text-amber-600 hover:border-amber-500 hover:bg-amber-50 transition-colors font-medium text-sm"
        >
          <Plus className="w-5 h-5" />
          {t('onboarding.step4Review.addQuestion')}
        </button>
      </div>

      <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-700">
          <strong>{t('onboarding.step4Review.tipLabel')}</strong>{t('onboarding.step4Review.tipText')}
        </p>
      </div>
    </div>
  );

  const currencySymbols: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };
  const getRewardIcon = (iconName: string) => {
    switch (iconName) {
      case 'star': return <Star className="w-5 h-5" />;
      case 'users': return <Users className="w-5 h-5" />;
      case 'video': return <Video className="w-5 h-5" />;
      case 'trending': return <TrendingUp className="w-5 h-5" />;
      case 'file': return <FileText className="w-5 h-5" />;
      default: return <Gift className="w-5 h-5" />;
    }
  };

  const renderStep4 = () => (
    <div>
      <div className="mb-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>{t('onboarding.step5Rewards.tipLabel')}</strong>{t('onboarding.step5Rewards.tipText')}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">{t('onboarding.step5Rewards.currency')}</label>
          <select
            value={rewardCurrency}
            onChange={(e) => setRewardCurrency(e.target.value as 'EUR' | 'USD' | 'GBP')}
            className="w-full max-w-xs px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent"
          >
            <option value="EUR">{t('onboarding.step5Rewards.currencyEUR')}</option>
            <option value="USD">{t('onboarding.step5Rewards.currencyUSD')}</option>
            <option value="GBP">{t('onboarding.step5Rewards.currencyGBP')}</option>
          </select>
        </div>
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('onboarding.step5Rewards.rewardAmounts')}</h3>
          {rewardConfigs.length === 0 ? (
            <p className="text-gray-500 text-sm">{t('onboarding.step5Rewards.loadingRewards')}</p>
          ) : (
            [...rewardConfigs].sort((a, b) => {
              const order = ['REVIEW', 'REFERENCE_CALL', 'VIDEO', 'USE_CASE', 'REFERRAL'];
              const ai = order.indexOf(a.type);
              const bi = order.indexOf(b.type);
              return (ai === -1 ? order.length : ai) - (bi === -1 ? order.length : bi);
            }).map((config) => {
              const trigger = config.reward_policy_trigger || 'LEAD_SUBMISSION';
              const isReferral = config.type === 'REFERRAL';
              const isBoth = isReferral && trigger === 'BOTH';
              const rewardMethod = (config as any).reward_method || 'GIFT_CARD';

              const methodToggle = (
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                  <button
                    type="button"
                    onClick={() => handleRewardMethodToggle(config.id, 'GIFT_CARD')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      rewardMethod === 'GIFT_CARD'
                        ? 'bg-white text-green-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <CreditCard className="w-3 h-3" />
                      <span>{t('onboarding.step5Rewards.giftCard')}</span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRewardMethodToggle(config.id, 'DISCOUNT')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      rewardMethod === 'DISCOUNT'
                        ? 'bg-white text-orange-700 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <Gift className="w-3 h-3" />
                      <span>{t('onboarding.step5Rewards.discount')}</span>
                    </div>
                  </button>
                </div>
              );

              const discountDisplay = (
                <div className="flex items-center gap-2">
                  {(config as any).discount_value ? (
                    <span className="text-sm text-orange-700 font-medium">
                      {(config as any).discount_type === 'percentage'
                        ? t('onboarding.step5Rewards.percentOff', { value: (config as any).discount_value })
                        : (config as any).discount_type === 'credit_points'
                        ? t('onboarding.step5Rewards.creditPointsValue', { value: (config as any).discount_value })
                        : (config as any).discount_type === 'free_months'
                        ? t('onboarding.step5Rewards.freeMonthsValue', { count: (config as any).discount_value })
                        : t('onboarding.step5Rewards.percentOff', { value: (config as any).discount_value })}
                      {(config as any).discount_product_name && ` ${t('onboarding.step5Rewards.onProduct', { product: (config as any).discount_product_name })}`}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400 italic">{t('onboarding.step5Rewards.notConfigured')}</span>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setDiscountConfigId(config.id);
                      setShowDiscountModal(true);
                    }}
                    className="px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors text-xs font-medium"
                  >
                    {t('onboarding.step5Rewards.configureDiscount')}
                  </button>
                </div>
              );

              return (
                <div key={config.id} className="p-4 border border-gray-200 rounded-lg space-y-3">
                  {/* Row 1: icon + name/description — and method/amount for non-REFERRAL */}
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color || 'bg-gray-100'}`}>
                        {getRewardIcon(config.icon)}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">{config.name}</p>
                        <p className="text-sm text-gray-600 truncate">{config.description}</p>
                      </div>
                    </div>
                    {!isReferral && (
                      <div className="flex items-center gap-3 flex-shrink-0">
                        {methodToggle}
                        {rewardMethod === 'GIFT_CARD' && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-sm">{currencySymbols[rewardCurrency]}</span>
                            <input
                              type="number"
                              value={config.amount || ''}
                              onChange={(e) => handleRewardConfigAmountChange(config.id, parseInt(e.target.value, 10) || 0)}
                              min={0}
                              step={5}
                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
                            />
                          </div>
                        )}
                        {rewardMethod === 'DISCOUNT' && discountDisplay}
                      </div>
                    )}
                    {isReferral && (
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-gray-400">
                          {trigger === 'LEAD_SUBMISSION' ? t('onboarding.step5Rewards.leadSubmission') : trigger === 'DEAL_WON' ? t('onboarding.step5Rewards.dealWon') : t('onboarding.step5Rewards.bothStages')}
                          {!isBoth && rewardMethod === 'GIFT_CARD' && config.amount ? ` · ${currencySymbols[rewardCurrency]}${config.amount}` : ''}
                          {!isBoth && rewardMethod === 'DISCOUNT' && (config as any).discount_value ? ` · ${t('onboarding.step5Rewards.discount')}` : ''}
                        </span>
                        <button
                          type="button"
                          onClick={() => { setReferralConfigModalId(config.id); setShowReferralConfigModal(true); }}
                          className="px-3 py-1.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3730a3] transition-colors text-xs font-medium"
                        >
                          {t('onboarding.step5Rewards.configure')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <DiscountRewardModal
        isOpen={showDiscountModal}
        onClose={() => { setShowDiscountModal(false); setDiscountConfigId(null); setDiscountStage('single'); }}
        onSave={handleDiscountSave}
        editingReward={
          discountConfigId
            ? (() => {
                const cfg = rewardConfigs.find(c => c.id === discountConfigId);
                if (!cfg) return null;
                if (discountStage === 'lead') {
                  if (!cfg.lead_submission_discount_value) return null;
                  return {
                    discountType: cfg.lead_submission_discount_type || 'percentage',
                    discountValue: cfg.lead_submission_discount_value,
                    productName: cfg.lead_submission_discount_product_name || '',
                  };
                }
                if (discountStage === 'deal') {
                  if (!cfg.deal_won_discount_value) return null;
                  return {
                    discountType: cfg.deal_won_discount_type || 'percentage',
                    discountValue: cfg.deal_won_discount_value,
                    productName: cfg.deal_won_discount_product_name || '',
                  };
                }
                if (!(cfg as any).discount_value) return null;
                return {
                  discountType: (cfg as any).discount_type || 'percentage',
                  discountValue: (cfg as any).discount_value || 0,
                  productName: (cfg as any).discount_product_name || '',
                };
              })()
            : null
        }
        products={products.filter(p => p.name.trim())}
      />

      {/* Referral Reward Config Modal */}
      {showReferralConfigModal && referralConfigModalId && (() => {
        const config = rewardConfigs.find(c => c.id === referralConfigModalId);
        if (!config) return null;
        const trigger = config.reward_policy_trigger || 'LEAD_SUBMISSION';
        const isBoth = trigger === 'BOTH';
        const sym = currencySymbols[rewardCurrency];
        const availableProducts = products.filter(p => p.name.trim());

        const DISCOUNT_TYPES = [
          { value: 'percentage', title: t('onboarding.step5Rewards.percentage'), suffix: '%' },
          { value: 'credit_points', title: t('onboarding.step5Rewards.creditPoints'), suffix: 'pts' },
          { value: 'free_months', title: t('onboarding.step5Rewards.freeMonths'), suffix: 'mo' },
        ] as const;

        const renderStageBlock = (stage: 'single' | 'lead' | 'deal') => {
          const method = isBoth
            ? (stage === 'lead' ? (config.lead_submission_reward_method || 'GIFT_CARD') : (config.deal_won_reward_method || 'GIFT_CARD'))
            : (config.reward_method || 'GIFT_CARD');
          const onMethodChange = (m: string) =>
            isBoth
              ? handleStageMethodChange(referralConfigModalId, stage as 'lead' | 'deal', m)
              : handleRewardMethodToggle(referralConfigModalId, m);

          const amount = isBoth
            ? (stage === 'lead' ? (config.lead_submission_amount ?? '') : (config.deal_won_amount ?? ''))
            : (config.amount || '');
          const onAmountChange = (val: number) =>
            isBoth
              ? handleRewardStageAmountChange(referralConfigModalId, stage as 'lead' | 'deal', val)
              : handleRewardConfigAmountChange(referralConfigModalId, val);

          const discType: string = (isBoth
            ? (stage === 'lead' ? config.lead_submission_discount_type : config.deal_won_discount_type)
            : (config as any).discount_type) || 'percentage';
          const discValue: number = (isBoth
            ? (stage === 'lead' ? config.lead_submission_discount_value : config.deal_won_discount_value)
            : (config as any).discount_value) || 0;
          const discProduct: string = (isBoth
            ? (stage === 'lead' ? config.lead_submission_discount_product_name : config.deal_won_discount_product_name)
            : (config as any).discount_product_name) || '';

          const suffix = DISCOUNT_TYPES.find(d => d.value === discType)?.suffix ?? '%';
          const previewLabel = discValue > 0
            ? `${discType === 'percentage' ? t('onboarding.step5Rewards.percentOff', { value: discValue }) : discType === 'credit_points' ? t('onboarding.step5Rewards.creditPointsValue', { value: discValue }) : t('onboarding.step5Rewards.freeMonthsValue', { count: discValue })}${discProduct.trim() ? ` ${t('onboarding.step5Rewards.onProduct', { product: discProduct.trim() })}` : ''}`
            : null;

          return (
            <div className="space-y-2.5">
              {/* Method toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5 w-fit">
                <button
                  type="button"
                  onClick={() => onMethodChange('GIFT_CARD')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${method === 'GIFT_CARD' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <div className="flex items-center gap-1">
                    <CreditCard className="w-3 h-3" />
                    <span>{t('onboarding.step5Rewards.giftCard')}</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => onMethodChange('DISCOUNT')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${method === 'DISCOUNT' ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  <div className="flex items-center gap-1">
                    <Gift className="w-3 h-3" />
                    <span>{t('onboarding.step5Rewards.discount')}</span>
                  </div>
                </button>
              </div>

              {/* Gift Card amount — animated */}
              <div style={{ maxHeight: method === 'GIFT_CARD' ? '56px' : '0px', opacity: method === 'GIFT_CARD' ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.2s ease, opacity 0.15s ease' }}>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-400">{sym}</span>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => onAmountChange(parseInt(e.target.value, 10) || 0)}
                    min={0}
                    step={5}
                    className="w-24 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#4339A0] focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Discount fields — animated */}
              <div style={{ maxHeight: method === 'DISCOUNT' ? '320px' : '0px', opacity: method === 'DISCOUNT' ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.25s ease, opacity 0.2s ease' }}>
                <div className="space-y-2.5">
                  {/* Discount type pills */}
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                    {DISCOUNT_TYPES.map((dt) => (
                      <button
                        key={dt.value}
                        type="button"
                        onClick={() => handleReferralDiscountChange(referralConfigModalId, stage, 'discount_type', dt.value)}
                        className={`flex-1 py-2 text-xs font-medium transition-all duration-150 ${
                          discType === dt.value ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                        } ${dt.value !== 'percentage' ? 'border-l border-gray-200' : ''}`}
                      >
                        {dt.title}
                      </button>
                    ))}
                  </div>

                  {/* Value + product side by side */}
                  <div className="flex items-end gap-2">
                    <div className="flex-shrink-0">
                      <label className="block text-xs text-gray-500 mb-1">{t('onboarding.step5Rewards.valueLabel')}</label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          value={discValue || ''}
                          onChange={(e) => {
                            const raw = parseFloat(e.target.value) || 0;
                            const val = discType === 'free_months' ? Math.max(0, Math.floor(raw)) : raw;
                            handleReferralDiscountChange(referralConfigModalId, stage, 'discount_value', val);
                          }}
                          min={0}
                          max={discType === 'percentage' ? 100 : undefined}
                          step={discType === 'free_months' ? 1 : undefined}
                          placeholder="0"
                          className="w-20 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                        />
                        <span className="text-xs text-gray-400">{suffix}</span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <label className="block text-xs text-gray-500 mb-1">{t('onboarding.step5Rewards.productService')}</label>
                      {availableProducts.length > 0 ? (
                        <select
                          value={discProduct}
                          onChange={(e) => handleReferralDiscountChange(referralConfigModalId, stage, 'discount_product_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                        >
                          <option value="">{t('onboarding.step5Rewards.selectProduct')}</option>
                          {availableProducts.map((p, i) => (
                            <option key={i} value={p.name}>{p.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={discProduct}
                          onChange={(e) => handleReferralDiscountChange(referralConfigModalId, stage, 'discount_product_name', e.target.value)}
                          placeholder={t('onboarding.step5Rewards.productPlaceholder')}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent text-sm"
                        />
                      )}
                    </div>
                  </div>

                  {/* Live preview pill */}
                  <div style={{ maxHeight: previewLabel ? '40px' : '0px', opacity: previewLabel ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.2s ease, opacity 0.15s ease' }}>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg w-fit">
                      <Gift className="w-3 h-3 text-orange-500 flex-shrink-0" />
                      <span className="text-xs text-orange-700 font-medium">{previewLabel}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        };

        return (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowReferralConfigModal(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 flex flex-col max-h-[90vh]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${config.color || 'bg-gray-100'}`}>
                    {getRewardIcon(config.icon)}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Configure Referral Reward</h3>
                    <p className="text-xs text-gray-400">{config.name}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowReferralConfigModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto px-6 py-5 space-y-5 flex-1">
                {/* When to reward */}
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-2">When to reward</p>
                  <div className="flex items-center bg-gray-100 rounded-lg p-0.5 w-fit">
                    {[
                      { value: 'LEAD_SUBMISSION', label: 'Lead Submission' },
                      { value: 'DEAL_WON', label: 'Deal Won' },
                      { value: 'BOTH', label: 'Both' },
                    ].map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handleRewardTriggerChange(referralConfigModalId, opt.value)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${trigger === opt.value ? 'bg-white text-[#4339A0] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Single stage — animated */}
                <div style={{ maxHeight: !isBoth ? '500px' : '0px', opacity: !isBoth ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.25s ease, opacity 0.2s ease' }}>
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Reward</p>
                    {renderStageBlock('single')}
                  </div>
                </div>

                {/* Both stages — animated */}
                <div style={{ maxHeight: isBoth ? '900px' : '0px', opacity: isBoth ? 1 : 0, overflow: 'hidden', transition: 'max-height 0.3s ease, opacity 0.2s ease' }}>
                  <div className="space-y-3">
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
                        <span className="w-4 h-4 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-[10px] font-bold">1</span>
                        At Lead Submission
                      </p>
                      {renderStageBlock('lead')}
                    </div>
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-xs font-semibold text-gray-600 mb-3 flex items-center gap-1.5">
                        <span className="w-4 h-4 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-[10px] font-bold">2</span>
                        At Deal Won
                      </p>
                      {renderStageBlock('deal')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end px-6 py-4 border-t border-gray-100 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowReferralConfigModal(false)}
                  className="px-5 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3730a3] transition-colors text-sm font-medium"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );

  const [expandedRecapSection, setExpandedRecapSection] = useState<string | null>(null);

  const toggleRecapSection = (section: string) => {
    setExpandedRecapSection(prev => prev === section ? null : section);
  };

  const renderStep5 = () => {
    const productCount = noProducts ? 0 : products.filter(p => p.name.trim()).length;
    // Mirror the save filter exactly — anything counted here but not savable
    // would silently disappear right after completing onboarding.
    const productNames = products.map(p => p.name.trim()).filter(Boolean);
    const savableUseCaseQuestions = useCaseQuestions.filter(q => isQuestionSavable(q, productNames));
    const savableReviewQuestions = reviewQuestions.filter(q => isQuestionSavable(q, productNames));
    const questionCount = savableUseCaseQuestions.length;
    const reviewQuestionCount = savableReviewQuestions.length;
    const rewardsConfigured = rewardConfigs.filter(c => c.amount > 0).length;

    const recapSections = [
      {
        id: 'profile',
        icon: <Building2 className="w-4 h-4" />,
        title: 'Company Information',
        badge: step1Form.companyName || step1Form.first_name ? '✓ Filled' : '⚠ Incomplete',
        badgeColor: (step1Form.companyName || step1Form.first_name) ? 'text-green-700 bg-green-50' : 'text-amber-700 bg-amber-50',
        content: (
          <div className="grid grid-cols-2 gap-3 text-sm mt-3">
            <div><span className="text-gray-500">Name:</span> <span className="font-medium">{step1Form.first_name} {step1Form.last_name}</span></div>
            <div><span className="text-gray-500">Company:</span> <span className="font-medium">{step1Form.companyName || '—'}</span></div>
            <div><span className="text-gray-500">Country:</span> <span className="font-medium">{step1Form.country || '—'}</span></div>
            <div><span className="text-gray-500">Website:</span> <span className="font-medium">{step1Form.website || '—'}</span></div>
            <div><span className="text-gray-500">Logo:</span> <span className="font-medium">{step1Form.logo ? '✓ Set' : '—'}</span></div>
            <div><span className="text-gray-500">LinkedIn:</span> <span className="font-medium">{step1Form.vendorLinkedin ? '✓ Set' : '—'}</span></div>
          </div>
        ),
      },
      {
        id: 'products',
        icon: <Package className="w-4 h-4" />,
        title: 'Products / Services',
        badge: noProducts ? 'Skipped' : `${productCount} product${productCount !== 1 ? 's' : ''}`,
        badgeColor: noProducts ? 'text-gray-600 bg-gray-100' : productCount > 0 ? 'text-green-700 bg-green-50' : 'text-amber-700 bg-amber-50',
        content: noProducts ? (
          <p className="text-sm text-gray-500 mt-3 italic">Skipped — your company doesn't sell different products.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {products.filter(p => p.name.trim()).map((p, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className="w-5 h-5 bg-[#4339A0] text-white rounded-full flex items-center justify-center text-xs flex-shrink-0 mt-0.5">{i + 1}</span>
                <div><span className="font-medium">{p.name}</span>{p.description && <span className="text-gray-500"> — {p.description.slice(0, 60)}{p.description.length > 60 ? '…' : ''}</span>}</div>
              </div>
            ))}
            {productCount === 0 && <p className="text-sm text-amber-600">No products added yet.</p>}
          </div>
        ),
      },
      {
        id: 'reviewQuestions',
        icon: <Star className="w-4 h-4" />,
        title: 'Review Questions',
        badge: `${reviewQuestionCount} question${reviewQuestionCount !== 1 ? 's' : ''}`,
        badgeColor: reviewQuestionCount >= 3 ? 'text-green-700 bg-green-50' : reviewQuestionCount > 0 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50',
        content: (
          <div className="mt-3 space-y-1.5">
            {savableReviewQuestions.map((q, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className="text-gray-400 flex-shrink-0 mt-0.5">{i + 1}.</span>
                <span className="text-gray-700">{q.question}</span>
                <span className="ml-auto flex-shrink-0 text-xs text-gray-400 capitalize">{q.answerType.replace('_', ' ')}</span>
              </div>
            ))}
            {reviewQuestionCount === 0 && <p className="text-sm text-red-600">No review questions added yet.</p>}
          </div>
        ),
      },
      {
        id: 'questions',
        icon: <FileText className="w-4 h-4" />,
        title: 'Use Case Questions',
        badge: `${questionCount} question${questionCount !== 1 ? 's' : ''}`,
        badgeColor: questionCount >= 7 ? 'text-green-700 bg-green-50' : questionCount > 0 ? 'text-amber-700 bg-amber-50' : 'text-red-700 bg-red-50',
        content: (
          <div className="mt-3 space-y-1.5">
            {savableUseCaseQuestions.map((q, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <span className="text-gray-400 flex-shrink-0 mt-0.5">{i + 1}.</span>
                <span className="text-gray-700">{q.question}</span>
                <span className="ml-auto flex-shrink-0 text-xs text-gray-400 capitalize">{q.answerType.replace('_', ' ')}</span>
              </div>
            ))}
            {questionCount === 0 && <p className="text-sm text-red-600">No questions added yet.</p>}
          </div>
        ),
      },
      {
        id: 'rewards',
        icon: <Gift className="w-4 h-4" />,
        title: 'Reward Configuration',
        badge: `${rewardsConfigured} reward${rewardsConfigured !== 1 ? 's' : ''} set · ${rewardCurrency}`,
        badgeColor: rewardsConfigured > 0 ? 'text-green-700 bg-green-50' : 'text-gray-600 bg-gray-100',
        content: (
          <div className="mt-3 space-y-1.5">
            {rewardConfigs.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{c.name}</span>
                <span className={`font-medium ${c.amount > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                  {c.amount > 0 ? `${currencySymbols[rewardCurrency]}${c.amount}` : 'Not set'}
                </span>
              </div>
            ))}
          </div>
        ),
      },
    ];

    return (
      <div>
        <div className="mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-800">
              <strong>Almost done!</strong> Review your setup below before going to the dashboard. Click any section to expand it.
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-[#4339A0]">{productCount}</div>
            <div className="text-xs text-gray-500 mt-0.5">Products</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className={`text-2xl font-bold ${reviewQuestionCount >= 3 ? 'text-green-600' : 'text-amber-500'}`}>{reviewQuestionCount}</div>
            <div className="text-xs text-gray-500 mt-0.5">Review Q.</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className={`text-2xl font-bold ${questionCount >= 7 ? 'text-green-600' : 'text-amber-500'}`}>{questionCount}</div>
            <div className="text-xs text-gray-500 mt-0.5">Use Case Q.</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-[#4339A0]">{rewardsConfigured}</div>
            <div className="text-xs text-gray-500 mt-0.5">Rewards set</div>
          </div>
        </div>

        {/* Expandable recap sections */}
        <div className="space-y-3">
          {recapSections.map((section) => (
            <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleRecapSection(section.id)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-[#4339A0] rounded-lg flex items-center justify-center text-white flex-shrink-0">
                    {section.icon}
                  </div>
                  <span className="font-medium text-gray-900">{section.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${section.badgeColor}`}>{section.badge}</span>
                </div>
                {expandedRecapSection === section.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {expandedRecapSection === section.id && (
                <div className="px-5 pb-5 border-t border-gray-100">
                  {section.content}
                  <button
                    type="button"
                    onClick={() => setCurrentStep((['profile', 'products', 'reviewQuestions', 'questions', 'rewards'] as string[]).indexOf(section.id) + 1 as OnboardingStep)}
                    className="mt-4 text-xs text-[#4339A0] hover:underline"
                  >
                    ← Go back to edit this step
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {updateVendorError && (
          <div className="mt-4 flex items-start gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Failed to save</p>
              <p>{(updateVendorError as { data?: { message?: string }; message?: string })?.data?.message ?? (updateVendorError as { message?: string }).message ?? 'Please try again.'}</p>
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={handleComplete}
            disabled={isProcessing || isUpdatingVendor}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {(isProcessing || isUpdatingVendor) ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <span>Go to Dashboard</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoadingVendor) {
      return (
        <div className="flex flex-col items-center justify-center py-24">
          <Loader2 className="w-12 h-12 text-[#4339A0] animate-spin mb-4" />
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      );
    }
    if (vendorError) {
      return (
        <div className="flex flex-col items-center justify-center py-24 px-4">
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 max-w-md">
            <AlertCircle className="w-6 h-6 flex-shrink-0" />
            <div>
              <p className="font-medium">Failed to load your profile</p>
              <p className="text-sm mt-1">{(vendorError as { data?: { message?: string } })?.data?.message ?? 'Please try again.'}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => refetchVendor()}
            className="mt-4 px-4 py-2 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f]"
          >
            Retry
          </button>
        </div>
      );
    }
    return (
      <>
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2Products()}
        {currentStep === 3 && renderStep4Review()}
        {currentStep === 4 && renderStep3()}
        {currentStep === 5 && renderStep4()}
        {currentStep === 6 && renderStep5()}
      </>
    );
  };

  const sidebarSteps = [
    { num: 1, label: 'Company Information', desc: 'Verify your company profile' },
    { num: 2, label: 'Add Product', desc: 'Products for your public page' },
    { num: 3, label: 'Review Questions', desc: 'Create questions for customers' },
    { num: 4, label: 'Use Case Questions', desc: 'Create questions for promoters' },
    { num: 5, label: 'Reward Config', desc: 'Set reward amounts' },
    { num: 6, label: 'Complete Setup', desc: 'Launch your promoter program' },
  ];

  const stepHeaders: Record<number, { icon: React.ReactNode; title: string; subtitle: string; iconBg: string }> = {
    1: { icon: <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />, title: 'Company & contact information', subtitle: 'Verify your company profile and contact details', iconBg: 'bg-[#4339A0]' },
    2: { icon: <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />, title: 'Add Product', subtitle: 'Add your products or solutions for your public page', iconBg: 'bg-[#4339A0]' },
    3: { icon: <Star className="w-5 h-5 sm:w-6 sm:h-6 text-white" />, title: 'Review Questions', subtitle: 'Set up questions customers answer when leaving a review', iconBg: 'bg-amber-500' },
    4: { icon: <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-white" />, title: 'Use Case Questions', subtitle: 'Create questions with multiple choice answers for promoters', iconBg: 'bg-[#4339A0]' },
    5: { icon: <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-white" />, title: 'Reward Configuration', subtitle: 'Set amounts for each promoter activity (optional)', iconBg: 'bg-[#4339A0]' },
    6: { icon: <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-white" />, title: 'All Set!', subtitle: "You're ready to start building your promoter network", iconBg: 'bg-green-600' },
  };

  const currentHeader = stepHeaders[currentStep];

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="flex flex-col lg:grid lg:grid-cols-5 h-screen min-h-0">

        {/* Mobile top bar */}
        <div className="lg:hidden shrink-0 bg-gradient-to-r from-[#4339A0] to-[#5a4bbf] text-white px-4 py-3">
          <div className="flex items-center justify-between mb-2">
            <img src="/logo-clareefai-white.png" alt="Clareefai" className="h-7 w-auto" />
            {currentStep < 6 && (
              <button type="button" onClick={handleCompleteLater} className="text-sm font-medium border border-white/50 text-white px-3 py-1.5 rounded-lg hover:bg-white/10 transition-colors">
                Complete later
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-1.5 bg-white/20 rounded-full">
              <div className="h-1.5 bg-white rounded-full transition-all duration-500" style={{ width: `${(currentStep / 6) * 100}%` }} />
            </div>
            <span className="text-xs text-white/80 whitespace-nowrap">Step {currentStep}/6</span>
          </div>
        </div>

        {/* Desktop left sidebar */}
        <div className="hidden lg:flex lg:col-span-2 min-h-0 overflow-hidden flex-col justify-between bg-gradient-to-br from-[#4339A0] to-[#5a4bbf] text-white p-8 xl:p-10">
          <div>
            <div className="mb-6">
              <img src="/logo-clareefai-white.png" alt="Clareefai" className="h-10 w-auto" />
            </div>

            <h1 className="text-3xl xl:text-4xl font-bold mb-3">Welcome to Clareefai!</h1>
            <p className="text-lg text-blue-100 mb-4">
              Let's set up your promoter program in 6 simple steps
            </p>

            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4 mb-8">
              <p className="text-sm text-blue-50">
                This setup takes about 30 minutes and it's worth doing properly. You'll configure your profile, products, use case questions, and rewards so your team can match prospects to the right promoters, collect stronger reviews, and start generating trusted proof fast.
              </p>
            </div>

            <div className="space-y-5">
              {sidebarSteps.map((step) => (
                <div key={step.num} className="flex items-start space-x-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all text-sm font-semibold ${
                    currentStep >= step.num ? 'bg-white text-[#4339A0]' : 'bg-white/20 text-white/60'
                  }`}>
                    {currentStep > step.num ? <Check className="w-5 h-5" /> : step.num}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-0.5 text-sm">{step.label}</h3>
                    <p className="text-xs text-blue-100">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <div className="h-2 bg-white/20 rounded-full mb-3">
              <div
                className="h-2 bg-white rounded-full transition-all duration-500"
                style={{ width: `${(currentStep / 6) * 100}%` }}
              />
            </div>
            <p className="text-sm text-blue-100">
              Step {currentStep} of 6 - {Math.round((currentStep / 6) * 100)}% Complete
            </p>
          </div>
        </div>

        {/* Right content panel */}
        <div className="lg:col-span-3 min-h-0 flex flex-col">

          {/* Fixed header: complete later + step title */}
          <div className="shrink-0 px-4 sm:px-6 lg:px-8 pt-3 pb-3 bg-white border-b border-gray-100">
            <div className="hidden lg:flex justify-end mb-2">
              {currentStep < 6 ? (
                <button type="button" onClick={handleCompleteLater} className="text-sm font-medium border border-gray-300 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors">
                  Complete later
                </button>
              ) : (
                <div />
              )}
            </div>
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 sm:w-11 sm:h-11 ${currentHeader.iconBg} rounded-xl flex items-center justify-center shrink-0`}>
                {currentHeader.icon}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">{currentHeader.title}</h2>
                <p className="text-xs sm:text-sm text-gray-500 truncate">{currentHeader.subtitle}</p>
              </div>
            </div>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 lg:px-8 py-4">
            {renderContent()}
          </div>

          {/* Fixed footer: nav buttons */}
          {currentStep < 6 && !isLoadingVendor && !vendorError && (
            <div className="shrink-0 px-4 sm:px-6 lg:px-8 py-3 border-t border-gray-200 bg-white">
              <div className="flex justify-between items-center gap-2">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="flex items-center space-x-1.5 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Previous</span>
                </button>
                {validationAttempted && !canProceedFromStep(currentStep) && (
                  <p className="hidden sm:block text-xs text-red-500 text-center flex-1 px-2">
                    Complete required fields to continue
                  </p>
                )}
                <button
                  onClick={handleNext}
                  className="flex items-center space-x-1.5 px-5 py-2.5 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] transition-colors shadow-sm text-sm font-medium"
                >
                  <span>Continue</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
